import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CITIES, DISCIPLINES, STANCES } from "@/lib/constants";
import Navbar from "@/components/Navbar";
import FighterCard from "@/components/FighterCard";
import RequestModal from "@/components/RequestModal";
import RequestsInbox from "@/components/RequestsInbox";
import MyCamp from "@/components/MyCamp";
import SessionsView from "@/components/SessionsView";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Radar, SlidersHorizontal, RotateCcw } from "lucide-react";

const SPONSOR_IMG =
  "https://images.unsplash.com/photo-1561532325-7d5231a2dede?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const selCls = "bg-[#0d0d0d] border-[#27272a] text-white h-10 focus:ring-2 focus:ring-volt min-w-[130px]";

function FilterSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={selCls}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-[#161616] border-[#27272a] text-white">
        <SelectItem value="All" className="focus:bg-volt focus:text-black">All {placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o} className="focus:bg-volt focus:text-black">{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function FeedPage() {
  const { user } = useAuth();
  const [view, setView] = useState("radar");
  const [filters, setFilters] = useState({ city: "All", discipline: "All", stance: "All" });
  const [weight, setWeight] = useState([40, 120]);
  const [modalFighter, setModalFighter] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const reset = () => { setFilters({ city: "All", discipline: "All", stance: "All" }); setWeight([40, 120]); };

  const feed = useQuery({
    queryKey: ["fighters", filters, weight],
    queryFn: async () => {
      const params = {};
      if (filters.city !== "All") params.city = filters.city;
      if (filters.discipline !== "All") params.discipline = filters.discipline;
      if (filters.stance !== "All") params.stance = filters.stance;
      params.weight_min = weight[0];
      params.weight_max = weight[1];
      const { data } = await api.get("/fighters", { params });
      return data;
    },
  });

  const requests = useQuery({
    queryKey: ["requests"],
    queryFn: async () => (await api.get("/requests")).data,
  });

  const openRequest = (f) => { setModalFighter(f); setModalOpen(true); };

  const fighters = feed.data?.fighters || [];
  const pendingReceived = (requests.data?.received || []).filter((r) => r.status === "pending").length;

  return (
    <div className="min-h-screen relative z-10">
      <Navbar view={view} setView={setView} receivedCount={pendingReceived} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {view === "radar" ? (
          <>
            {/* Radar header */}
            <div className="relative overflow-hidden border border-[#27272a] rounded-sm bg-card tech-grid mb-6">
              <div className="relative z-10 p-6 sm:p-8 flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-volt">
                    <Radar size={20} />
                    <span className="text-xs uppercase tracking-[0.3em]">Sparring Radar</span>
                  </div>
                  <h1 className="font-heading font-black uppercase text-4xl sm:text-5xl tracking-tighter mt-1">
                    {fighters.length} Fighters<span className="text-volt">.</span>
                  </h1>
                  <p className="text-zinc-400 text-sm mt-1">
                    Scanning {filters.city === "All" ? "all UK regions" : filters.city} · your base: {user?.city}
                  </p>
                </div>
                <div className="relative w-24 h-24 hidden sm:flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full border border-volt/40 radar-ring" />
                  <div className="absolute inset-0 rounded-full border border-volt/30 radar-ring" style={{ animationDelay: "1.5s" }} />
                  <Radar size={40} className="text-volt" />
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-card border border-white/10 rounded-sm p-4 mb-8">
              <div className="flex items-center gap-2 mb-3 text-zinc-400">
                <SlidersHorizontal size={15} className="text-volt" />
                <span className="text-xs uppercase tracking-widest">Filter Partners</span>
              </div>
              <div className="flex flex-wrap items-end gap-4">
                <FilterSelect value={filters.city} onChange={set("city")} options={CITIES} placeholder="Regions" testid="filter-city" />
                <FilterSelect value={filters.discipline} onChange={set("discipline")} options={DISCIPLINES} placeholder="Disciplines" testid="filter-discipline" />
                <FilterSelect value={filters.stance} onChange={set("stance")} options={STANCES} placeholder="Stances" testid="filter-stance" />
                <div className="flex-1 min-w-[220px]">
                  <div className="flex justify-between text-xs text-zinc-400 mb-2 uppercase tracking-wider">
                    <span>Weight Range</span>
                    <span className="text-volt">{weight[0]}–{weight[1]} kg</span>
                  </div>
                  <Slider
                    data-testid="filter-weight"
                    min={40} max={120} step={1}
                    value={weight}
                    onValueChange={setWeight}
                    className="[&_[role=slider]]:bg-volt [&_[role=slider]]:border-volt [&>span>span]:bg-volt"
                  />
                </div>
                <button
                  data-testid="reset-filters"
                  onClick={reset}
                  className="flex items-center gap-1.5 h-10 px-3 border border-[#27272a] rounded-sm text-zinc-400 hover:text-white hover:border-volt/50 transition-colors duration-200 text-xs uppercase tracking-wider"
                >
                  <RotateCcw size={14} /> Reset
                </button>
              </div>
            </div>

            {/* Feed grid */}
            {feed.isLoading ? (
              <div className="text-center py-20 text-volt font-heading uppercase tracking-widest animate-pulse">Scanning…</div>
            ) : fighters.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-[#27272a] rounded-sm">
                <Radar size={32} className="mx-auto text-zinc-600 mb-3" />
                <p className="font-heading text-xl uppercase tracking-wide text-zinc-400">No fighters match your filters</p>
                <p className="text-sm text-zinc-600 mt-1">Widen the weight range or clear filters.</p>
              </div>
            ) : (
              <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="fighter-grid">
                {fighters.map((f) => (
                  <FighterCard key={f.id} fighter={f} onRequest={openRequest} />
                ))}
              </motion.div>
            )}

            {/* Sponsor slot */}
            <div
              data-testid="sponsor-slot"
              className="mt-10 relative overflow-hidden border border-volt/30 rounded-sm group"
            >
              <img src={SPONSOR_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-50 transition-opacity duration-300" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d] via-[#0d0d0d]/80 to-transparent" />
              <div className="relative z-10 p-6 sm:p-8 flex items-center justify-between gap-4">
                <div>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-volt">Official Gear Partner</span>
                  <h3 className="font-heading font-black uppercase text-2xl sm:text-3xl tracking-tight mt-1">
                    Kit up. Train harder.
                  </h3>
                  <p className="text-zinc-400 text-sm mt-1 max-w-sm">
                    Premium gloves, wraps and protection trusted by UK pros. Your gear, your edge.
                  </p>
                </div>
                <button
                  data-testid="sponsor-cta"
                  className="shrink-0 bg-volt text-black font-heading font-bold uppercase tracking-widest text-sm px-5 py-2.5 rounded-sm hover:bg-volt-hover transition-colors duration-200"
                >
                  Shop Gear
                </button>
              </div>
            </div>
          </>
        ) : view === "camp" ? (
          <MyCamp />
        ) : view === "sessions" ? (
          <SessionsView />
        ) : (
          <RequestsInbox data={requests.data} refetch={requests.refetch} />
        )}
      </main>

      <RequestModal fighter={modalFighter} open={modalOpen} onOpenChange={setModalOpen} />

      <footer className="max-w-7xl mx-auto px-4 sm:px-6 py-8 mt-6 border-t border-[#27272a]">
        <p className="text-xs text-zinc-600 leading-relaxed">
          SparBase is a digital networking directory only. SparBase takes zero liability for injuries
          or disputes. All users spar entirely at their own risk.
        </p>
      </footer>
    </div>
  );
}
